import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { NgApexchartsModule } from 'ng-apexcharts';
import { forkJoin, finalize } from 'rxjs';
import { resolveApiErrorMessage } from 'src/app/core/http/api-http-error';
import {
  LlmDashboardTotals,
  LlmAgentUsageRow,
  LlmModelUsageRow,
  LlmWorkflowUsageRow,
  LlmTenantCostRow,
  LlmMarginRow,
  LlmTrendPoint,
  LlmUsageApiService,
} from 'src/app/core/services/llm-usage-api.service';
import { PageSurfaceHeaderComponent } from 'src/app/shared/page-surface-header/page-surface-header.component';
import { ACTION_BUTTON } from 'src/app/shared/utils/action-button.classes';
import { DATE_RANGE_PRESETS, cleanFilters, compactTokens, formatUsd, rangeFor } from './llm-cost.filters';
import { downloadCsv, toCsv } from './csv-export.util';

/**
 * What the platform is spending on AI, and where it goes.
 *
 * Every figure here is computed server-side in exact decimal arithmetic and arrives as a
 * string; this component formats but never sums. Adding money in the browser is how a cost
 * report stops reconciling against the provider invoice.
 */
@Component({
  selector: 'app-llm-cost',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, NgApexchartsModule, PageSurfaceHeaderComponent],
  templateUrl: './llm-cost.component.html',
})
export class LlmCostComponent implements OnInit {
  readonly btn = ACTION_BUTTON;
  private readonly api = inject(LlmUsageApiService);

  readonly presets = DATE_RANGE_PRESETS;
  readonly formatUsd = formatUsd;
  readonly compactTokens = compactTokens;

  selectedPreset = '30d';

  /**
   * Extra filters, applied to every panel at once.
   *
   * The options come from the results themselves — a tenant or model only appears once it has
   * actually spent something, so the bar can never offer a filter that returns an empty screen.
   */
  filters: { tenant_id?: number; model_name?: string; provider?: string; task_code?: string; source?: string } = {};
  tenantOptions: { id: number; name: string }[] = [];
  modelOptions: string[] = [];
  readonly sources = ['PD', 'SIMULATE', 'PREVIEW', 'PROBE'];
  loading = false;
  error = '';

  totals: LlmDashboardTotals | null = null;
  trend: LlmTrendPoint[] = [];
  tenants: LlmTenantCostRow[] = [];
  models: LlmModelUsageRow[] = [];
  margin: LlmMarginRow[] = [];
  agents: LlmAgentUsageRow[] = [];
  workflows: LlmWorkflowUsageRow[] = [];
  unattributedCostInr = '0';

  ngOnInit(): void {
    this.load();
  }

  onPresetChange(key: string): void {
    this.selectedPreset = key;
    this.load();
  }

  load(): void {
    const preset = this.presets.find((p) => p.key === this.selectedPreset) ?? this.presets[1];
    const range = rangeFor(preset.days);
    const filters = { ...range, ...cleanFilters(this.filters) };
    this.loading = true;
    this.error = '';

    forkJoin({
      dashboard: this.api.dashboard(filters),
      trend: this.api.trend({ from: range.from.slice(0, 10), to: range.to.slice(0, 10) }),
      tenants: this.api.tenants(filters),
      models: this.api.models(filters),
      margin: this.api.margin(filters),
      agents: this.api.agents(filters),
      workflows: this.api.workflows(filters),
    })
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (res) => {
          this.totals = res.dashboard.data?.totals ?? null;
          this.trend = res.trend.data ?? [];
          this.tenants = res.tenants.data ?? [];
          this.models = res.models.data ?? [];
          this.margin = res.margin.data?.data ?? [];
          this.unattributedCostInr = res.margin.data?.unattributed_ai_cost_inr ?? '0';
          this.agents = res.agents.data ?? [];
          this.workflows = res.workflows.data ?? [];
          this.refreshOptions();
        },
        error: (err) => (this.error = (resolveApiErrorMessage(err) || 'Could not load LLM cost data.')),
      });
  }

  /** Only ever widen the option lists: filtering to one tenant must not empty the dropdown. */
  private refreshOptions(): void {
    for (const row of this.tenants) {
      if (row.tenant_id != null && !this.tenantOptions.some((o) => o.id === row.tenant_id)) {
        this.tenantOptions.push({ id: row.tenant_id, name: row.tenant_name });
      }
    }
    for (const row of this.models) {
      if (!this.modelOptions.includes(row.model_name)) this.modelOptions.push(row.model_name);
    }
  }

  applyFilters(): void {
    this.load();
  }

  resetFilters(): void {
    this.filters = {};
    this.load();
  }

  get hasFilters(): boolean {
    return Object.values(this.filters).some((v) => v !== undefined && v !== null && v !== '');
  }

  /** Daily spend — the series an operator watches for a step change after a config edit. */
  get costChart() {
    return {
      series: [{ name: 'Cost (USD)', data: this.trend.map((point) => Number(point.total_cost_usd)) }],
      chart: { type: 'area' as const, height: 260, toolbar: { show: false }, fontFamily: 'inherit' },
      dataLabels: { enabled: false },
      stroke: { curve: 'smooth' as const, width: 2 },
      xaxis: { categories: this.trend.map((point) => point.date) },
      yaxis: { labels: { formatter: (value: number) => `$${value.toFixed(4)}` } },
      colors: ['#4361ee'],
      tooltip: { y: { formatter: (value: number) => `$${value.toFixed(6)}` } },
      grid: { borderColor: '#e0e6ed' },
    };
  }

  /** Daily token volume, beside cost: a cost rise with flat tokens is a pricing or model change. */
  get tokenChart() {
    return {
      series: [{ name: 'Tokens', data: this.trend.map((point) => point.total_tokens) }],
      chart: { type: 'bar' as const, height: 260, toolbar: { show: false }, fontFamily: 'inherit' },
      dataLabels: { enabled: false },
      plotOptions: { bar: { borderRadius: 4, columnWidth: '55%' } },
      xaxis: { categories: this.trend.map((point) => point.date) },
      colors: ['#00ab55'],
      grid: { borderColor: '#e0e6ed' },
    };
  }

  exportMargin(): void {
    const headers = [
      { key: 'tenant_name', label: 'Tenant' },
      { key: 'billed_pds', label: 'Billed PDs' },
      { key: 'revenue_inr', label: 'Revenue (INR)' },
      { key: 'ai_cost_inr', label: 'AI Cost (INR)' },
      { key: 'margin_inr', label: 'Margin (INR)' },
      { key: 'margin_pct', label: 'Margin %' },
      { key: 'ai_cost_pct_of_revenue', label: 'AI Cost % of Revenue' },
      { key: 'cost_is_complete', label: 'Cost Complete' },
    ];
    downloadCsv(
      `llm-margin-${new Date().toISOString().slice(0, 10)}.csv`,
      toCsv(headers, this.margin as unknown as Record<string, unknown>[]),
    );
  }

  exportTenants(): void {
    const headers = [
      { key: 'tenant_name', label: 'Tenant' },
      { key: 'pds', label: 'PDs' },
      { key: 'calls', label: 'LLM Calls' },
      { key: 'input_tokens', label: 'Input Tokens' },
      { key: 'output_tokens', label: 'Output Tokens' },
      { key: 'total_tokens', label: 'Total Tokens' },
      { key: 'total_cost_usd', label: 'Total Cost (USD)' },
      { key: 'avg_cost_per_pd_usd', label: 'Avg Cost / PD (USD)' },
    ];
    downloadCsv(`llm-tenant-cost-${new Date().toISOString().slice(0, 10)}.csv`, toCsv(headers, this.tenants as unknown as Record<string, unknown>[]));
  }

  exportModels(): void {
    const headers = [
      { key: 'model_name', label: 'Model' },
      { key: 'calls', label: 'Calls' },
      { key: 'total_tokens', label: 'Total Tokens' },
      { key: 'total_cost_usd', label: 'Total Cost (USD)' },
      { key: 'cost_pct', label: 'Cost %' },
      { key: 'avg_cost_per_call_usd', label: 'Avg Cost / Call (USD)' },
    ];
    downloadCsv(`llm-model-usage-${new Date().toISOString().slice(0, 10)}.csv`, toCsv(headers, this.models as unknown as Record<string, unknown>[]));
  }
}
